import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { removeFromCart, updateQuantity, clearCart, selectCartTotal } from '../../store/slices/cartSlice';
import api from '../../services/api';
import {
  Box, Typography, Button, Paper, Grid, IconButton, Divider, Stepper, Step,
  StepLabel, TextField, Checkbox, FormControlLabel, Alert, CircularProgress,
  Card, CardContent, Chip, Radio, RadioGroup, FormControl, FormLabel,
  Avatar, InputAdornment, Tooltip, LinearProgress, Collapse
} from '@mui/material';
import {
  Delete, Add, Remove, ShoppingCartOutlined, LocalShipping, Build,
  CreditCard, AccountBalance, QrCode2, PaymentsOutlined, CheckCircle,
  ArrowBack, ArrowForward, Place, Inventory2, Payment, DoneAll,
  ContentCopy, Warning, Star, Wallet
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
const ASSET_BASE_URL = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

const STEPS = ['Cart Review', 'Shipping Address', 'Services', 'Payment Method', 'Confirm Order'];

const PAYMENT_METHODS = [
  { id: 'bank_transfer', label: 'Bank Transfer', icon: <AccountBalance sx={{ fontSize: 32 }} />, color: '#1565C0', desc: 'Direct wire transfer to OSESG bank account' },
  { id: 'bank_card',    label: 'Bank Card',     icon: <CreditCard sx={{ fontSize: 32 }} />,    color: '#2E7D32', desc: 'Visa / MasterCard / AMEX' },
  { id: 'paypal',       label: 'PayPal',        icon: <PaymentsOutlined sx={{ fontSize: 32 }} />, color: '#003087', desc: 'Pay via PayPal balance or card' },
  { id: 'alipay',       label: 'Alipay',        icon: <QrCode2 sx={{ fontSize: 32 }} />,       color: '#00A1E9', desc: '支付宝 - Scan QR to pay' },
  { id: 'wechat_pay',   label: 'WeChat Pay',    icon: <QrCode2 sx={{ fontSize: 32 }} />,       color: '#07C160', desc: '微信支付 - Scan QR to pay' },
];

const statusColors = {
  pending: '#FF9800', confirmed: '#2196F3', processing: '#9C27B0',
  manufacturing: '#FF5722', quality_check: '#795548', ready_to_ship: '#00BCD4',
  in_transit_domestic: '#3F51B5', at_warehouse: '#607D8B', shipped_international: '#009688',
  customs_clearance: '#F44336', delivered: '#4CAF50', installed: '#8BC34A',
  completed: '#4CAF50', cancelled: '#9E9E9E', disputed: '#F44336'
};

// ─── Step 0: Cart Review ──────────────────────────────────────────────────────
const CartReview = ({ items, onRemove, onQty, total }) => {
  if (items.length === 0) {
    return (
      <Box textAlign="center" py={6}>
        <ShoppingCartOutlined sx={{ fontSize: 80, color: 'text.disabled' }} />
        <Typography variant="h6" color="text.secondary" mt={2}>Your cart is empty</Typography>
      </Box>
    );
  }
  return (
    <Box>
      {items.map((item, idx) => (
        <Paper key={`${item.productId}-${item.customization?.requestId || idx}`} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={2}>
              {item.image
                ? <Box component="img" src={item.image} alt={item.name} sx={{ width: '100%', maxHeight: 80, objectFit: 'contain', borderRadius: 1 }} />
                : <Box sx={{ width: '100%', height: 80, bgcolor: '#f0f0f0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Inventory2 color="disabled" />
                  </Box>
              }
            </Grid>
            <Grid item xs={12} sm={5}>
              <Typography variant="subtitle1" fontWeight={700}>{item.name}</Typography>
              {item.nameAr && <Typography variant="caption" color="text.secondary" dir="rtl">{item.nameAr}</Typography>}
              <Chip label={item.osesgCode} size="small" variant="outlined" sx={{ mt: 0.5, fontSize: '0.7rem' }} />
              {item.specifications && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  Note: {item.specifications}
                </Typography>
              )}
              {item.moq > 1 && (
                <Typography variant="caption" color="warning.main">Min Order: {item.moq} units</Typography>
              )}
              {item.customization && (
                <Paper variant="outlined" sx={{ mt: 1, p: 1, borderRadius: 1.5, bgcolor: '#f3e8ff', borderColor: '#c9a8f0' }}>
                  <Typography variant="caption" fontWeight={700} color="secondary.dark" display="block">
                    + Customization: {item.customization.description}
                  </Typography>
                  <Typography variant="caption" color="secondary.dark">
                    +${item.customization.price.toLocaleString()}
                  </Typography>
                </Paper>
              )}
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small"
                  onClick={() => onQty(item.productId, item.quantity - 1, item.customization?.requestId)}
                  disabled={item.quantity <= Math.max(1, item.moq)}>
                  <Remove fontSize="small" />
                </IconButton>
                <Typography fontWeight={600} minWidth={32} textAlign="center">{item.quantity}</Typography>
                <IconButton size="small" onClick={() => onQty(item.productId, item.quantity + 1, item.customization?.requestId)}>
                  <Add fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Typography fontWeight={700} color="secondary.main">
                ${item.totalPrice.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ${item.unitPrice.toLocaleString()} / unit
              </Typography>
            </Grid>
            <Grid item xs={2} sm={1} sx={{ textAlign: 'right' }}>
              <Tooltip title="Remove item">
                <IconButton size="small" color="error" onClick={() => onRemove(item.productId, item.customization?.requestId)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
      ))}
      <Paper sx={{ p: 2, mt: 2, bgcolor: '#fff8e1', borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>Subtotal ({items.reduce((s, i) => s + i.quantity, 0)} items)</Typography>
          <Typography variant="h5" color="secondary.main" fontWeight={800}>
            ${total.toLocaleString()}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Final price confirmed after order processing. Shipping, customs & additional services added in next steps.
        </Typography>
      </Paper>
    </Box>
  );
};

// ─── Step 1: Shipping Address ─────────────────────────────────────────────────
const ShippingForm = ({ data, onChange }) => (
  <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Place color="primary" /> Delivery Address
      </Typography>
    </Grid>
    {[
      { label: 'Contact Name *', field: 'contactName', xs: 12, sm: 6 },
      { label: 'Phone Number *', field: 'phone', xs: 12, sm: 6 },
      { label: 'Street Address *', field: 'address', xs: 12 },
      { label: 'City *', field: 'city', xs: 12, sm: 4 },
      { label: 'State / Province', field: 'state', xs: 12, sm: 4 },
      { label: 'Postal Code', field: 'postalCode', xs: 12, sm: 4 },
      { label: 'Country *', field: 'country', xs: 12 },
    ].map(({ label, field, xs, sm }) => (
      <Grid item xs={xs} sm={sm} key={field}>
        <TextField fullWidth label={label} size="small" value={data[field] || ''}
          onChange={(e) => onChange(field, e.target.value)} />
      </Grid>
    ))}
  </Grid>
);

// ─── Step 2: Services ─────────────────────────────────────────────────────────
const ServicesForm = ({ data, onChange }) => {
  const services = [
    { key: 'shipping',          icon: <LocalShipping color="primary" />,     label: 'International Shipping',    desc: 'Door-to-door freight handled by OSESG logistics partners' },
    { key: 'customsClearance',  icon: <Inventory2 color="warning" />,         label: 'Customs Clearance',         desc: 'Full import documentation, duties & compliance handled for you' },
    { key: 'installation',      icon: <Build color="secondary" />,            label: 'Installation & Setup',      desc: 'Professional installation by certified engineers at your site' },
    { key: 'technicalSupport',  icon: <CheckCircle color="success" />,        label: 'Technical Support (1 Year)', desc: 'Priority technical support and warranty coverage for 12 months' },
  ];
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Additional Services (Optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        OSESG manages all logistics end-to-end. Select the services you require:
      </Typography>
      <Grid container spacing={2}>
        {services.map(({ key, icon, label, desc }) => (
          <Grid item xs={12} sm={6} key={key}>
            <Paper
              variant="outlined"
              onClick={() => onChange(key, !data[key])}
              sx={{
                p: 2, cursor: 'pointer', borderRadius: 2, transition: 'all 0.2s',
                borderColor: data[key] ? 'primary.main' : 'divider',
                bgcolor: data[key] ? 'primary.50' : 'background.paper',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' }
              }}>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Checkbox checked={!!data[key]} size="small" sx={{ p: 0 }} readOnly />
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    {icon}
                    <Typography fontWeight={600}>{label}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">{desc}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ─── Step 3: Payment Method ───────────────────────────────────────────────────
const PaymentForm = ({ method, setMethod, payDetails, setPayDetails }) => {
  const copy = (text) => { navigator.clipboard.writeText(text); toast.info('Copied!', { autoClose: 1500 }); };
  const [savedMethods, setSavedMethods] = useState([]);
  const [savedLoading, setSavedLoading] = useState(true);
  const [usingSaved, setUsingSaved] = useState(null); // ID of selected saved method
  const [companyQr, setCompanyQr] = useState(null);
  const savedQrUrl = payDetails._savedQr ? `${ASSET_BASE_URL}${payDetails._savedQr}` : null;
  // The company's own receiving QR (set by an admin in Settings) is what a
  // customer should actually scan to pay OSESG — prefer it over any QR the
  // customer saved to their own payment methods.
  const alipayQrDisplayUrl = companyQr?.alipayQrUrl ? `${ASSET_BASE_URL}${companyQr.alipayQrUrl}` : savedQrUrl;
  const wechatQrDisplayUrl = companyQr?.wechatQrUrl ? `${ASSET_BASE_URL}${companyQr.wechatQrUrl}` : savedQrUrl;

  useEffect(() => {
    api.get('/payment-methods')
      .then(({ data }) => setSavedMethods(data.data || []))
      .catch(() => {})
      .finally(() => setSavedLoading(false));
    api.get('/payment-methods/company-qr')
      .then(({ data }) => setCompanyQr(data.data))
      .catch(() => {});
  }, []);

  const applySaved = (m) => {
    setUsingSaved(m._id);
    setMethod(m.type);
    const details = {};
    if (m.type === 'bank_card') {
      details.cardName  = m.cardName  || '';
      details.cardLast4 = m.cardLast4 || '';
      details.expiry    = m.cardExpiry || '';
      details.cardType  = m.cardType  || 'visa';
    } else if (m.type === 'paypal') {
      details.paypalEmail = m.paypalEmail || '';
    } else if (m.type === 'wechat_pay' || m.type === 'alipay') {
      details.transactionId = '';
      details._savedQr = m.qrCodeImage || null;
      details._accountId = m.accountId || '';
    } else if (m.type === 'bank_transfer') {
      details.reference = '';
    }
    setPayDetails(details);
  };

  const clearSaved = () => {
    setUsingSaved(null);
    setMethod('bank_transfer');
    setPayDetails({});
  };

  const savedInfo = (m) => {
    const t = PAYMENT_METHODS.find((x) => x.id === m.type);
    switch (m.type) {
      case 'bank_card':    return `${(m.cardType||'CARD').toUpperCase()} •••• ${m.cardLast4||'????'}`;
      case 'paypal':       return m.paypalEmail;
      case 'wechat_pay':   return m.accountId ? `WeChat: ${m.accountId}` : 'WeChat Pay';
      case 'alipay':       return m.accountId ? `Alipay: ${m.accountId}` : 'Alipay';
      case 'bank_transfer':return m.bankName || 'Bank Transfer';
      default:             return t?.label || m.type;
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Select Payment Method
      </Typography>

      {/* Saved Methods */}
      {!savedLoading && savedMethods.length > 0 && (
        <Box mb={3}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Wallet fontSize="small" color="primary" />
            <Typography variant="body2" fontWeight={600} color="primary.main">Saved Payment Methods</Typography>
          </Box>
          <Grid container spacing={1}>
            {savedMethods.map((m) => {
              const pmInfo = PAYMENT_METHODS.find((x) => x.id === m.type);
              const isSelected = usingSaved === m._id;
              return (
                <Grid item xs={12} sm={6} key={m._id}>
                  <Paper
                    onClick={() => applySaved(m)}
                    variant="outlined"
                    sx={{
                      p: 1.5, cursor: 'pointer', borderRadius: 2,
                      border: '2px solid',
                      borderColor: isSelected ? pmInfo?.color || 'primary.main' : '#e0e0e0',
                      bgcolor: isSelected ? `${pmInfo?.color || '#1976d2'}12` : 'background.paper',
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: pmInfo?.color || 'primary.main' }
                    }}>
                    <Avatar sx={{
                      bgcolor: isSelected ? pmInfo?.color || 'primary.main' : '#f0f0f0',
                      color: isSelected ? 'white' : pmInfo?.color || 'primary.main',
                      width: 36, height: 36
                    }}>
                      {pmInfo?.icon}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={700} noWrap>{m.label}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{savedInfo(m)}</Typography>
                    </Box>
                    {m.isDefault && <Chip label="Default" size="small" icon={<Star />} color="primary" sx={{ fontSize: '0.65rem' }} />}
                    {isSelected && <CheckCircle sx={{ color: pmInfo?.color || 'primary.main', flexShrink: 0 }} />}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
          {usingSaved && (
            <Button size="small" sx={{ mt: 1 }} onClick={clearSaved}>
              Use a different method
            </Button>
          )}
          <Divider sx={{ mt: 2, mb: 2 }}>
            <Chip label="or choose below" size="small" />
          </Divider>
        </Box>
      )}

      {/* Method Cards */}
      <Grid container spacing={2} mb={3}>
        {PAYMENT_METHODS.map((pm) => (
          <Grid item xs={6} sm={4} key={pm.id}>
            <Paper
              onClick={() => setMethod(pm.id)}
              sx={{
                p: 2, cursor: 'pointer', borderRadius: 2, textAlign: 'center',
                border: '2px solid', transition: 'all 0.2s',
                borderColor: method === pm.id ? pm.color : '#e0e0e0',
                bgcolor: method === pm.id ? `${pm.color}15` : 'background.paper',
                '&:hover': { borderColor: pm.color, bgcolor: `${pm.color}10` }
              }}>
              <Box sx={{ color: pm.color, mb: 0.5 }}>{pm.icon}</Box>
              <Typography variant="body2" fontWeight={700}>{pm.label}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">{pm.desc}</Typography>
              {method === pm.id && (
                <CheckCircle sx={{ color: pm.color, fontSize: 18, mt: 0.5 }} />
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ mb: 2 }} />

      {/* Bank Transfer Details */}
      {method === 'bank_transfer' && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Transfer the order amount to the account below. Include your Order Number as reference.
          </Alert>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>OSESG Bank Account Details</Typography>
            {[
              { label: 'Bank Name',    value: 'International Commerce Bank (ICB)' },
              { label: 'Account Name', value: 'OSESG Trading Platform LLC' },
              { label: 'Account No.',  value: '8821-0044-9900-3712' },
              { label: 'SWIFT / BIC',  value: 'ICBGUSD2XXX' },
              { label: 'IBAN',         value: 'GB29ICBG60161331926819' },
              { label: 'Currency',     value: 'USD' },
            ].map(({ label, value }) => (
              <Box key={label} display="flex" justifyContent="space-between" alignItems="center" py={0.5}
                sx={{ borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight={600}>{value}</Typography>
                  <IconButton size="small" onClick={() => copy(value)}><ContentCopy fontSize="small" /></IconButton>
                </Box>
              </Box>
            ))}
          </Paper>
          <TextField fullWidth label="Your Transfer Reference / Receipt Number" size="small" sx={{ mt: 2 }}
            value={payDetails.reference || ''}
            onChange={(e) => setPayDetails({ ...payDetails, reference: e.target.value })}
            helperText="Enter your bank transfer reference number to speed up order confirmation" />
        </Box>
      )}

      {/* Bank Card */}
      {method === 'bank_card' && (
        <Box>
          <Alert severity="info" icon={<Warning />} sx={{ mb: 2 }}>
            Card payment is processed securely offline. Our team will contact you to complete payment after order review.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Cardholder Name" size="small"
                value={payDetails.cardName || ''} onChange={(e) => setPayDetails({ ...payDetails, cardName: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start"><CreditCard fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Card Number (last 4 digits only)" size="small"
                inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
                value={payDetails.cardLast4 || ''} onChange={(e) => setPayDetails({ ...payDetails, cardLast4: e.target.value.replace(/\D/g, '') })}
                helperText="For verification purposes only. Full card details collected securely by our finance team." />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Expiry (MM/YY)" size="small"
                value={payDetails.expiry || ''} onChange={(e) => setPayDetails({ ...payDetails, expiry: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Card Type" size="small"
                select SelectProps={{ native: true }}
                value={payDetails.cardType || 'visa'} onChange={(e) => setPayDetails({ ...payDetails, cardType: e.target.value })}>
                {['visa', 'mastercard', 'amex', 'unionpay'].map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
              </TextField>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Alipay */}
      {method === 'alipay' && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Scan the QR code below using your Alipay app (支付宝). After payment, enter your Alipay transaction ID.
          </Alert>
          <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#00A1E915', borderRadius: 2, mb: 2 }}>
            {alipayQrDisplayUrl ? (
              <>
                <Box
                  component="img"
                  src={alipayQrDisplayUrl}
                  alt="Alipay QR Code"
                  sx={{ maxHeight: 220, maxWidth: '100%', borderRadius: 2, border: '1px solid #b6d9ea', bgcolor: 'white', p: 1 }}
                />
                {companyQr?.alipayAccountName && (
                  <Typography variant="body2" color="text.secondary" mt={1}>{companyQr.alipayAccountName}</Typography>
                )}
              </>
            ) : (
              <>
                <QrCode2 sx={{ fontSize: 120, color: '#00A1E9' }} />
                <Typography variant="body2" color="text.secondary" mt={1}>Alipay QR Code</Typography>
                <Typography variant="caption" color="text.secondary">Not configured yet — please choose another payment method.</Typography>
              </>
            )}
          </Box>
          <TextField fullWidth label="Alipay Transaction ID" size="small"
            value={payDetails.transactionId || ''}
            onChange={(e) => setPayDetails({ ...payDetails, transactionId: e.target.value })}
            helperText="Enter your Alipay payment transaction ID after completing payment" />
        </Box>
      )}

      {/* WeChat Pay */}
      {method === 'wechat_pay' && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Scan the QR code below using WeChat (微信). After payment, enter your WeChat Order No.
          </Alert>
          <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#07C16015', borderRadius: 2, mb: 2 }}>
            {wechatQrDisplayUrl ? (
              <>
                <Box
                  component="img"
                  src={wechatQrDisplayUrl}
                  alt="WeChat Pay QR Code"
                  sx={{ maxHeight: 220, maxWidth: '100%', borderRadius: 2, border: '1px solid #b7e2cb', bgcolor: 'white', p: 1 }}
                />
                {companyQr?.wechatAccountName && (
                  <Typography variant="body2" color="text.secondary" mt={1}>{companyQr.wechatAccountName}</Typography>
                )}
              </>
            ) : (
              <>
                <QrCode2 sx={{ fontSize: 120, color: '#07C160' }} />
                <Typography variant="body2" color="text.secondary" mt={1}>WeChat Pay QR Code</Typography>
                <Typography variant="caption" color="text.secondary">Not configured yet — please choose another payment method.</Typography>
              </>
            )}
          </Box>
          <TextField fullWidth label="WeChat Transaction ID / Order No." size="small"
            value={payDetails.transactionId || ''}
            onChange={(e) => setPayDetails({ ...payDetails, transactionId: e.target.value })}
            helperText="Enter your WeChat Pay order number after completing payment" />
        </Box>
      )}

      {/* PayPal */}
      {method === 'paypal' && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Pay via PayPal to our business account. After payment, enter your PayPal transaction ID.
          </Alert>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#003087' + '15', mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom color="#003087">PayPal Business Account</Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body1" fontWeight={600}>payments@osesg-platform.com</Typography>
              <IconButton size="small" onClick={() => copy('payments@osesg-platform.com')}><ContentCopy fontSize="small" /></IconButton>
            </Box>
          </Paper>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Your PayPal Email (for records)" size="small"
                value={payDetails.paypalEmail || ''}
                onChange={(e) => setPayDetails({ ...payDetails, paypalEmail: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="PayPal Transaction ID" size="small"
                value={payDetails.transactionId || ''}
                onChange={(e) => setPayDetails({ ...payDetails, transactionId: e.target.value })}
                helperText="Enter PayPal Transaction ID after completing payment" />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

// ─── Step 4: Order Review ─────────────────────────────────────────────────────
const OrderReview = ({ items, address, services, method, notes, setNotes, total }) => {
  const pm = PAYMENT_METHODS.find(p => p.id === method);
  const selectedServices = Object.entries(services).filter(([, v]) => v).map(([k]) => k);
  const serviceLabels = {
    shipping: 'International Shipping', customsClearance: 'Customs Clearance',
    installation: 'Installation & Setup', technicalSupport: 'Technical Support'
  };

  return (
    <Box>
      <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
        Almost done! Review your order details before placing.
      </Alert>

      {/* Items */}
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>Order Items</Typography>
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        {items.map((item, i) => (
          <Box key={`${item.productId}-${item.customization?.requestId || i}`} sx={{ px: 2, py: 1.5, borderBottom: i < items.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
            <Box display="flex" justifyContent="space-between">
              <Box>
                <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                <Typography variant="caption" color="text.secondary">{item.osesgCode} · Qty: {item.quantity}</Typography>
                {item.customization && (
                  <Typography variant="caption" color="secondary.dark" display="block">
                    + Customization: {item.customization.description} (+${item.customization.price.toLocaleString()})
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" fontWeight={700}>${item.totalPrice.toLocaleString()}</Typography>
            </Box>
          </Box>
        ))}
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#fff8e1' }}>
          <Box display="flex" justifyContent="space-between">
            <Typography fontWeight={700}>Subtotal</Typography>
            <Typography fontWeight={800} color="secondary.main">${total.toLocaleString()}</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">+ shipping, taxes & services (to be confirmed)</Typography>
        </Box>
      </Paper>

      {/* Shipping */}
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>Delivery Address</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="body2">{address.contactName} | {address.phone}</Typography>
        <Typography variant="body2">{address.address}, {address.city} {address.postalCode}</Typography>
        <Typography variant="body2">{address.state}, {address.country}</Typography>
      </Paper>

      {/* Services */}
      {selectedServices.length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Additional Services</Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            {selectedServices.map(s => <Chip key={s} label={serviceLabels[s]} color="primary" size="small" icon={<CheckCircle />} />)}
          </Box>
        </>
      )}

      {/* Payment */}
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>Payment Method</Typography>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ color: pm?.color }}>{pm?.icon}</Box>
        <Box>
          <Typography fontWeight={700}>{pm?.label}</Typography>
          <Typography variant="caption" color="text.secondary">{pm?.desc}</Typography>
        </Box>
      </Paper>

      {/* Notes */}
      <TextField
        fullWidth multiline rows={3} label="Order Notes (optional)" size="small"
        placeholder="Special instructions, delivery notes, custom requirements..."
        value={notes} onChange={(e) => setNotes(e.target.value)}
      />
    </Box>
  );
};

// ─── Success Screen ───────────────────────────────────────────────────────────
const OrderSuccess = ({ orderNumber, navigate }) => (
  <Box textAlign="center" py={4}>
    <CheckCircle sx={{ fontSize: 80, color: 'success.main' }} />
    <Typography variant="h5" fontWeight={800} mt={2}>Order Placed Successfully!</Typography>
    <Typography variant="h6" color="text.secondary" mt={1}>Order Number:</Typography>
    <Chip label={orderNumber} color="primary" sx={{ fontSize: '1rem', px: 2, py: 1, mt: 1 }} />
    <Typography variant="body2" color="text.secondary" mt={2} maxWidth={500} mx="auto">
      Your order is now visible to the OSESG team and the assigned supplier. You will receive updates
      at every step of the process. Track your order in My Orders.
    </Typography>
    <Box mt={3} display="flex" gap={2} justifyContent="center">
      <Button variant="contained" onClick={() => navigate('/customer/orders')}>View My Orders</Button>
      <Button variant="outlined" onClick={() => navigate('/products')}>Continue Shopping</Button>
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const CustomerCart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector(s => s.cart.items);
  const total = useSelector(selectCartTotal);

  const [step, setStep] = useState(0);
  const [placing, setPlacing] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);

  const [address, setAddress] = useState({
    contactName: '', phone: '', address: '', city: '', state: '', country: '', postalCode: ''
  });
  const [services, setServices] = useState({
    shipping: false, customsClearance: false, installation: false, technicalSupport: false
  });
  const [payMethod, setPayMethod] = useState('bank_transfer');
  const [payDetails, setPayDetails] = useState({});
  const [notes, setNotes] = useState('');

  const updateAddress = (field, val) => setAddress(prev => ({ ...prev, [field]: val }));
  const updateService = (key, val) => setServices(prev => ({ ...prev, [key]: val }));

  const canNext = () => {
    if (step === 0) return cartItems.length > 0;
    if (step === 1) return address.contactName && address.phone && address.address && address.city && address.country;
    return true;
  };

  const handlePlaceOrder = async () => {
    setPlacing(true);
    try {
      const payload = {
        items: cartItems.map(item => ({
          product: item.productId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          specifications: item.specifications,
          customizationRequestId: item.customization?.requestId || undefined
        })),
        shippingAddress: address,
        services,
        notes,
        paymentMethod: payMethod,
        paymentDetails: payDetails,
        quoteId: cartItems.find(i => i.quoteId)?.quoteId || null
      };
      const { data } = await api.post('/orders', payload);
      dispatch(clearCart());
      setPlacedOrder(data.data);
      setStep(5);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to place order. Please try again.');
    }
    setPlacing(false);
  };

  if (step === 5 && placedOrder) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>Checkout</Typography>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <OrderSuccess orderNumber={placedOrder.orderNumber} navigate={navigate} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {step === 0 ? 'Shopping Cart' : 'Checkout'}
      </Typography>

      {/* Progress Stepper */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((label, i) => (
            <Step key={label} completed={i < step}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {placing && <LinearProgress sx={{ mt: 1 }} />}
      </Paper>

      <Grid container spacing={3}>
        {/* Main content */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            {step === 0 && (
              <CartReview items={cartItems}
                onRemove={(id, customizationRequestId) => dispatch(removeFromCart({ productId: id, customizationRequestId: customizationRequestId || null }))}
                onQty={(id, qty, customizationRequestId) => dispatch(updateQuantity({ productId: id, quantity: qty, customizationRequestId: customizationRequestId || null }))}
                total={total} />
            )}
            {step === 1 && <ShippingForm data={address} onChange={updateAddress} />}
            {step === 2 && <ServicesForm data={services} onChange={updateService} />}
            {step === 3 && <PaymentForm method={payMethod} setMethod={setPayMethod} payDetails={payDetails} setPayDetails={setPayDetails} />}
            {step === 4 && <OrderReview items={cartItems} address={address} services={services}
              method={payMethod} notes={notes} setNotes={setNotes} total={total} />}
          </Paper>
        </Grid>

        {/* Order Summary Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 3, position: 'sticky', top: 80 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Order Summary</Typography>
            <Divider sx={{ mb: 1.5 }} />
            {cartItems.map((item, i) => (
              <Box key={`${item.productId}-${item.customization?.requestId || i}`} display="flex" justifyContent="space-between" py={0.75}>
                <Typography variant="body2" sx={{ maxWidth: '65%' }} noWrap>{item.name} ×{item.quantity}</Typography>
                <Typography variant="body2" fontWeight={600}>${item.totalPrice.toLocaleString()}</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1.5 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight={700}>Subtotal</Typography>
              <Typography fontWeight={800} color="secondary.main">${total.toLocaleString()}</Typography>
            </Box>
            {Object.entries(services).some(([, v]) => v) && (
              <Box mt={1}>
                {Object.entries(services).filter(([, v]) => v).map(([k]) => (
                  <Box key={k} display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      {k === 'shipping' ? 'Shipping' : k === 'customsClearance' ? 'Customs' : k === 'installation' ? 'Installation' : 'Tech Support'}
                    </Typography>
                    <Typography variant="caption" color="success.main">Included</Typography>
                  </Box>
                ))}
              </Box>
            )}
            {step >= 3 && (
              <Box mt={1.5} display="flex" alignItems="center" gap={1}>
                {PAYMENT_METHODS.find(p => p.id === payMethod)?.icon && (
                  <Box sx={{ color: PAYMENT_METHODS.find(p => p.id === payMethod)?.color, display: 'flex' }}>
                    {React.cloneElement(PAYMENT_METHODS.find(p => p.id === payMethod).icon, { sx: { fontSize: 20 } })}
                  </Box>
                )}
                <Typography variant="caption">{PAYMENT_METHODS.find(p => p.id === payMethod)?.label}</Typography>
              </Box>
            )}

            <Divider sx={{ my: 1.5 }} />

            {/* Navigation buttons */}
            <Box display="flex" gap={1} flexDirection="column">
              {step < 4 ? (
                <Button
                  variant="contained" fullWidth size="large"
                  disabled={!canNext()}
                  endIcon={<ArrowForward />}
                  onClick={() => setStep(s => s + 1)}>
                  {step === 0 ? 'Proceed to Checkout' : 'Continue'}
                </Button>
              ) : (
                <Button
                  variant="contained" color="success" fullWidth size="large"
                  disabled={placing || cartItems.length === 0}
                  startIcon={placing ? <CircularProgress size={16} /> : <DoneAll />}
                  onClick={handlePlaceOrder}>
                  {placing ? 'Placing Order...' : 'Place Order'}
                </Button>
              )}
              {step > 0 && (
                <Button variant="outlined" fullWidth startIcon={<ArrowBack />}
                  onClick={() => setStep(s => s - 1)}>
                  Back
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerCart;
